// Node type definitions for the Agent Workflow Builder
export type AgentNodeType = 'trigger' | 'query' | 'aiAction' | 'condition' | 'apiCall' | 'notify' | 'tool';

export interface NodeConfig {
    type: AgentNodeType;
    label: string;
    icon: string;
    color: string;
    bgColor: string;
    borderColor: string;
    description: string;
    category: 'input' | 'process' | 'output' | 'tools';
    defaultData: Record<string, any>;
}

export const NODE_CONFIGS: Record<AgentNodeType, NodeConfig> = {
    trigger: {
        type: 'trigger',
        label: 'Trigger',
        icon: '⚡',
        color: '#7C3AED',
        bgColor: '#F5F3FF',
        borderColor: '#C4B5FD',
        description: 'Start your workflow from a channel or schedule',
        category: 'input',
        defaultData: {
            label: 'Trigger',
            triggerType: 'whatsapp',  // whatsapp | teams | cron | manual
            description: 'Incoming message',
            cronExpression: '',
        },
    },
    query: {
        type: 'query',
        label: 'Query',
        icon: '🔍',
        color: '#2563EB',
        bgColor: '#EFF6FF',
        borderColor: '#93C5FD',
        description: 'Execute a Sentinel query to read data',
        category: 'process',
        defaultData: {
            label: 'Execute Query',
            queryId: '',
            queryName: '',
            description: 'Fetch data from query-service',
            parameters: [],
        },
    },
    aiAction: {
        type: 'aiAction',
        label: 'AI Action',
        icon: '🤖',
        color: '#059669',
        bgColor: '#ECFDF5',
        borderColor: '#6EE7B7',
        description: 'Let AI understand intent and make decisions',
        category: 'process',
        defaultData: {
            label: 'AI Action',
            prompt: '',
            promptSections: {
                roleContext: '',
                schemaContext: '',
                businessRules: '',
                validationChecklist: '',
                preExecutionChecks: '',
                errorHandling: '',
            },
            description: 'Process with AI',
            model: 'gpt-4o-mini',
            temperature: 0.3,
        },
    },
    condition: {
        type: 'condition',
        label: 'Condition',
        icon: '🔀',
        color: '#D97706',
        bgColor: '#FFFBEB',
        borderColor: '#FCD34D',
        description: 'Branch based on a condition',
        category: 'process',
        defaultData: {
            label: 'Condition',
            field: '',
            operator: 'equals',
            value: '',
            description: 'Check condition',
        },
    },
    apiCall: {
        type: 'apiCall',
        label: 'API Call',
        icon: '🔧',
        color: '#DC2626',
        bgColor: '#FEF2F2',
        borderColor: '#FCA5A5',
        description: 'Call an external REST API to take action',
        category: 'process',
        defaultData: {
            label: 'API Call',
            url: '',
            method: 'POST',
            description: 'Call external API',
            headers: {},
            body: '',
        },
    },
    notify: {
        type: 'notify',
        label: 'Notify',
        icon: '📤',
        color: '#0891B2',
        bgColor: '#ECFEFF',
        borderColor: '#67E8F9',
        description: 'Send response back on the same channel',
        category: 'output',
        defaultData: {
            label: 'Send Response',
            channel: 'same',  // same | whatsapp | teams | email
            description: 'Reply to user',
            messageTemplate: '',
        },
    },
    tool: {
        type: 'tool',
        label: 'Tool',
        icon: '🛠️',
        color: '#4B5563',
        bgColor: '#F3F4F6',
        borderColor: '#9CA3AF',
        description: 'Provide an external capability to an AI Agent',
        category: 'tools',
        defaultData: {
            label: 'Tools Group',
            enabledTools: [],
            description: 'Configure multiple tool settings',
        },
    },
};
